var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import { FlatList } from 'react-native';
import { ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Tooltip } from 'recharts';
import { Button, createStyleSheet, Divider, Text, useStyleSheet, View } from '@gopeerproject/ui-kit';
import { Colors } from '../../constants';
import { ChartFrame } from '../ChartFrame';
import { CustomTooltip } from '../CustomTooltip';
export const PieChart = (_a) => {
    var { data = [{ value: 0, label: 'Placeholder' }], onItemClick, showLegend = true, sortLegend = false, roundTo = 0, valuePrefix = '', height } = _a, baseChartProps = __rest(_a, ["data", "onItemClick", "showLegend", "sortLegend", "roundTo", "valuePrefix", "height"]);
    const styles = useStyleSheet(getStyles);
    const description = useMemo(() => {
        const total = data.reduce((res, curr) => res + curr.value, 0);
        return `${valuePrefix}${total.toFixed(roundTo)}`;
    }, [data, valuePrefix, roundTo]);
    const formattedData = useMemo(() => {
        const arr = data.slice();
        if (sortLegend)
            arr.sort((a, b) => b.value - a.value);
        return arr;
    }, [data, sortLegend]);
    const isEmpty = !formattedData.some((d) => d.value > 0);
    const renderItem = ({ item }) => {
        var _a, _b;
        return (React.createElement(View, null,
            React.createElement(Divider, { type: 'horizontal' }),
            React.createElement(View, { style: styles.row },
                React.createElement(View, { style: styles.legendLabelContainer },
                    React.createElement(View, { style: [styles.dot, { backgroundColor: item.color }] }),
                    React.createElement(View, { style: styles.textContainer },
                        React.createElement(Text, { type: 'body', weight: 'regular', size: 'lg', style: styles.textContainer }, item.value))),
                React.createElement(View, { style: styles.value },
                    React.createElement(Text, { type: 'body', weight: 'semibold', size: 'lg' },
                        valuePrefix, (_b = (_a = item.payload) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 :
                        _b.toFixed(roundTo))))));
    };
    const renderCustomItem = ({ item }) => {
        var _a, _b, _c;
        return (React.createElement(View, null,
            React.createElement(Divider, { type: 'horizontal' }),
            React.createElement(View, { style: onItemClick ? styles.customRow : styles.row },
                React.createElement(View, { style: styles.legendLabelContainer },
                    React.createElement(View, { style: [styles.dot, { backgroundColor: item.color }] }),
                    React.createElement(Text, { style: styles.legendLabel, type: 'body', weight: 'semibold', size: 'lg' },
                        valuePrefix, (_b = (_a = item.payload) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 :
                        _b.toFixed(roundTo)),
                    React.createElement(Text, { type: 'body', weight: 'regular', size: 'lg' }, item.value)),
                ((_c = data.find((d) => d.label === item.value)) === null || _c === void 0 ? void 0 : _c.customItem) && (React.createElement(Button, { type: 'tertiary', accent: 'neutral', size: 'xs', text: 'View', rightIcon: 'chevron-right', onPress: () => {
                        onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(data.find((d) => d.label === item.value).customItem);
                    } })))));
    };
    return (React.createElement(ChartFrame, Object.assign({ description: description, height: height !== null && height !== void 0 ? height : 480 }, baseChartProps),
        React.createElement(View, { style: { height: '100%', maxHeight: 240, opacity: isEmpty ? 0.42 : 1 } },
            isEmpty && (React.createElement(View, { style: {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center'
                } },
                React.createElement(Text, { type: 'body', weight: 'regular', size: 'md' }, "No data yet"))),
            React.createElement(ResponsiveContainer, { width: '100%', height: '100%' },
                React.createElement(RePieChart, null,
                    !isEmpty && (React.createElement(Tooltip, { content: React.createElement(CustomTooltip, { valuePrefix: valuePrefix, roundTo: roundTo }), cursor: false })),
                    React.createElement(Pie, { data: !isEmpty
                            ? formattedData
                            : formattedData.map((d) => ({ label: d.label, value: 1 })), dataKey: 'value', nameKey: 'label', innerRadius: 60, outerRadius: 90 }, formattedData.map((_, index) => {
                        var _a;
                        return (React.createElement(Cell, { fill: (_a = Colors[index]) !== null && _a !== void 0 ? _a : Colors[0], key: `cell-${index}` }));
                    }))))),
        showLegend && (React.createElement(FlatList, { data: formattedData.map((data, index) => {
                var _a;
                return ({
                    value: data.label,
                    color: (_a = Colors[index]) !== null && _a !== void 0 ? _a : Colors[0],
                    payload: { value: data.value }
                });
            }), keyExtractor: (_, index) => `${index}`, renderItem: data.some((d) => d.customItem) ? renderCustomItem : renderItem }))));
};
const getStyles = createStyleSheet(({ size }) => ({
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginVertical: size.G,
        maxWidth: '100% !important'
    },
    customRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginVertical: size.E
    },
    legendLabelContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1
        // width: '100%'
    },
    textContainer: {
        flexWrap: 'wrap',
        flexBasis: '80%'
        // flexBasis: 'auto',
        // maxWidth: `calc(50% - ${size.C}px)`
    },
    legendLabel: {
        width: size.M
    },
    dot: {
        width: size.C,
        height: size.C,
        margin: size.C,
        borderRadius: size.C
    },
    value: {
        flexBasis: '50px',
        justifyContent: 'flex-end',
        textAlign: 'right'
    }
}));
//# sourceMappingURL=index.js.map